import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Calendar, Receipt, RefreshCw, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { PedidoVista } from '@shared/types';
import { puedeUsarCanal } from '@shared/permisos';
import { useSessionStore } from '@renderer/store/useSessionStore';
import { useNegocioStore } from '@renderer/store/useNegocioStore';
import { formatPrecio } from '@renderer/utils/formatters';
import { DetallePedidoModal } from '@renderer/components/pedidos/DetallePedidoModal';

/**
 * Sprint 145 — **"¿Dónde veo lo que ya vendí?"**
 *
 * De la dueña, escribiendo mientras atendía: *"me equivoqué en los frijoles y no sé en qué
 * apartado hacerlo"*. Lo que fue a buscar, con esas palabras, es *dónde están mis ventas*.
 *
 * ---
 *
 * Sprint 147 — **El día es un día.**
 *
 * La lista nació atada al turno de caja, para no tener dos definiciones de "hoy" en pantallas de
 * dinero. El supuesto —que la caja se abre y se cierra cada jornada— **no se cumplió**: el dueño
 * mandó la foto del sistema real con la caja abierta desde hacía nueve días, el título *"Ventas
 * del día"* y el número grande en **$11,124**. Lo de ese día eran $5,120.
 *
 * Ese número no se mira por curiosidad: con él se decide si el día estuvo bueno, si alcanza para
 * pagarle al proveedor y cuánto debería haber en la caja. Equivocarlo por un factor de dos no es
 * un encabezado mal redactado.
 *
 * Tres decisiones, en orden de importancia:
 *
 *  1. **El número grande es el del periodo elegido**, y por defecto ese periodo es hoy.
 *  2. **Lo acumulado sin cortar no desaparece**: se dice aparte, con su nombre, y cuando lleva más
 *     de un día se convierte en un aviso — que es justo el problema que causó todo esto.
 *  3. **Cuando el periodo abarca varios días, cada venta lleva su fecha** y la lista se agrupa por
 *     jornada. Una hora suelta sin fecha es lo que hace creer que todo es de hoy.
 */

/** Periodos que se ofrecen. Cuatro, no más: esto se usa de pie y con gente esperando. */
type Periodo = 'hoy' | 'ayer' | 'semana' | 'rango';

/** `Date` → `AAAA-MM-DD` en hora local (no `toISOString`, que corre el día en husos negativos). */
function comoTexto(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function hace(dias: number): string {
  const d = new Date();
  d.setDate(d.getDate() - dias);
  return comoTexto(d);
}

export function VentasDelDiaPage() {
  const negocio = useNegocioStore((s) => s.negocio);
  const monedaSimbolo = negocio?.monedaSimbolo ?? '$';
  const locale = negocio?.locale ?? 'es-MX';
  /*
    El aviso de la caja abierta lo ve cualquiera que llegue a esta pantalla —es información del
    negocio—, pero el atajo para cerrar el día sólo se le ofrece a quien puede hacerlo. Mandar a
    caja a una pantalla que la va a rebotar es prometerle algo que no existe; y esconder el botón
    no autoriza nada: quien decide sigue siendo el servidor.
  */
  const sesion = useSessionStore((s) => s.sesion);
  const puedeCerrarElDia = !!sesion && puedeUsarCanal(sesion.rol, 'cierre-dia:ejecutar');

  const [periodo, setPeriodo] = useState<Periodo>('hoy');
  const [desdeTexto, setDesdeTexto] = useState(hace(6));
  const [hastaTexto, setHastaTexto] = useState(comoTexto(new Date()));

  const [ventas, setVentas] = useState<PedidoVista[]>([]);
  const [total, setTotal] = useState(0);
  const [truncado, setTruncado] = useState(false);
  const [turno, setTurno] = useState<{ desde: string; total: number; dias: number } | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busqueda, setBusqueda] = useState('');
  const [detalleId, setDetalleId] = useState<number | null>(null);

  /** El rango que se le pide al servidor. Él vuelve a validarlo: aquí sólo se arma. */
  const rango = useMemo(() => {
    if (periodo === 'hoy') return { desde: comoTexto(new Date()) };
    if (periodo === 'ayer') return { desde: hace(1), hasta: hace(1) };
    if (periodo === 'semana') return { desde: hace(6), hasta: comoTexto(new Date()) };
    return { desde: desdeTexto, hasta: hastaTexto };
  }, [periodo, desdeTexto, hastaTexto]);

  const cargar = useCallback(async () => {
    setCargando(true);
    const r = await window.api.pedidos.ventasDelDia(rango);
    if (r.ok) {
      setVentas(r.data.ventas);
      setTotal(r.data.total);
      setTruncado(r.data.truncado);
      setTurno(r.data.turno);
      setError(null);
    } else {
      /*
        Una lista vacía y una lista que no se pudo cargar se ven igual, y aquí eso importa: si
        alguien viene a buscar su venta para corregirla, "no hay ventas" es una respuesta que se
        cree. Se dice lo que pasó.
      */
      setError(r.error);
    }
    setCargando(false);
  }, [rango]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  /** Por folio o por nombre: las dos formas en que alguien recuerda una venta de hace un rato. */
  const filtradas = busqueda.trim()
    ? ventas.filter((v) => {
        const t = busqueda.trim().toLowerCase();
        return (
          String(v.numeroPedido).padStart(5, '0').includes(t) ||
          (v.clienteNombre ?? '').toLowerCase().includes(t)
        );
      })
    : ventas;

  /*
    Agrupar por jornada. Sólo hace falta cuando el periodo abarca más de un día, pero se calcula
    siempre: si el resultado es un solo grupo, no se dibuja ningún encabezado y la lista queda
    exactamente como antes.
  */
  const porDia = useMemo(() => {
    const mapa = new Map<string, PedidoVista[]>();
    for (const v of filtradas) {
      const clave = comoTexto(new Date(v.fechaCreacion));
      const y = mapa.get(clave);
      if (y) y.push(v);
      else mapa.set(clave, [v]);
    }
    return [...mapa.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [filtradas]);

  const hora = (iso: string) =>
    new Date(iso).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });

  const diaLargo = (texto: string) => {
    const [a, m, d] = texto.split('-').map(Number);
    const fecha = new Date(a, m - 1, d);
    const hoy = comoTexto(new Date());
    const etiqueta = fecha.toLocaleDateString(locale, {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
    });
    /*
      Sprint 156 — La lista es de lo COBRADO en el periodo, pero se agrupa por el día en que se
      tomó el pedido. Un pedido de ayer cobrado hoy caía bajo "viernes 25" y el dueño, sumando a
      mano, lo leyó como venta de ayer que no debería contar. El dinero entró hoy: se dice así.
    */
    if (rango.desde && texto < rango.desde) {
      const cuando =
        periodo === 'hoy' ? 'cobrados hoy' : periodo === 'ayer' ? 'cobrados ayer' : 'cobrados en este periodo';
      const de = texto === hace(1) ? `de ayer (${etiqueta})` : `del ${etiqueta}`;
      return `Pedidos ${de}, ${cuando}`;
    }
    if (texto === hoy) return `Hoy · ${etiqueta}`;
    if (texto === hace(1)) return `Ayer · ${etiqueta}`;
    return etiqueta;
  };

  /*
    "Del 2026-09-11 al 2026-09-13" es como habla una base de datos, no una persona. La fecha que
    se LEE se escribe con el día y el mes en letra; la que se TECLEA sigue siendo la del
    calendario del navegador, que ya sabe hacerlo en el idioma del equipo.
  */
  const fechaCorta = (texto: string) => {
    const [a, m, d] = texto.split('-').map(Number);
    if (!a || !m || !d) return texto;
    // El mes se traduce; el "N de" se arma aquí porque el formato corto del navegador devuelve
    // cosas como "11-septiembre" según el idioma del equipo, y eso no se lee.
    const mes = new Date(a, m - 1, d).toLocaleDateString(locale, { month: 'long' });
    return `${d} de ${mes}`;
  };

  const tituloPeriodo =
    periodo === 'hoy'
      ? 'Hoy'
      : periodo === 'ayer'
        ? 'Ayer'
        : periodo === 'semana'
          ? 'Últimos 7 días'
          : desdeTexto === hastaTexto
            ? fechaCorta(desdeTexto)
            : `Del ${fechaCorta(desdeTexto)} al ${fechaCorta(hastaTexto)}`;

  const botonPeriodo = (valor: Periodo, etiqueta: string) => (
    <button
      key={valor}
      type="button"
      onClick={() => setPeriodo(valor)}
      aria-pressed={periodo === valor}
      className={`min-h-[44px] rounded-lg border-2 px-4 text-sm font-semibold transition ${
        periodo === valor
          ? 'border-primary bg-primary text-white'
          : 'border-slate-300 bg-white text-slate-700 hover:border-primary'
      }`}
    >
      {etiqueta}
    </button>
  );

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center gap-3">
        <Receipt className="h-7 w-7 text-primary" aria-hidden="true" />
        <div className="flex-1">
          <h1 className="text-2xl font-bold">Ventas</h1>
          <p className="text-sm text-slate-600">Lo que llevas cobrado, por día</p>
        </div>
        <button onClick={() => void cargar()} className="btn-ghost min-h-[44px] text-sm">
          <RefreshCw className="h-4 w-4" aria-hidden="true" />
          Actualizar
        </button>
      </header>

      <div className="flex flex-wrap gap-2">
        {botonPeriodo('hoy', 'Hoy')}
        {botonPeriodo('ayer', 'Ayer')}
        {botonPeriodo('semana', 'Últimos 7 días')}
        {botonPeriodo('rango', 'Elegir fechas')}
      </div>

      {periodo === 'rango' && (
        <div className="card flex flex-wrap items-end gap-3">
          <div>
            <label className="label" htmlFor="ventasDesde">
              Desde
            </label>
            <input
              id="ventasDesde"
              type="date"
              className="input min-h-[44px]"
              value={desdeTexto}
              max={hastaTexto}
              onChange={(e) => setDesdeTexto(e.target.value)}
            />
          </div>
          <div>
            <label className="label" htmlFor="ventasHasta">
              Hasta
            </label>
            <input
              id="ventasHasta"
              type="date"
              className="input min-h-[44px]"
              value={hastaTexto}
              min={desdeTexto}
              max={comoTexto(new Date())}
              onChange={(e) => setHastaTexto(e.target.value)}
            />
          </div>
          <Calendar className="mb-3 h-5 w-5 text-slate-500" aria-hidden="true" />
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="card">
          <div className="text-sm font-semibold text-slate-600">Cobrado · {tituloPeriodo}</div>
          <div className="font-mono text-3xl font-bold">
            {formatPrecio(total, monedaSimbolo, locale)}
          </div>
        </div>
        <div className="card">
          <div className="text-sm font-semibold text-slate-600">Ventas</div>
          <div className="font-mono text-3xl font-bold">{ventas.length}</div>
        </div>
      </div>

      {/*
        Sprint 147 — La caja que lleva días abierta.

        Es la causa de que esta pantalla mintiera, y no se arregla escondiéndola: mientras no se
        corte, ese dinero sigue creciendo en un solo montón y el corte de caja no sirve para
        cuadrar nada. Se dice aquí, donde ella mira el dinero, con el camino a un clic.
      */}
      {turno && turno.dias > 1 && (
        <div className="rounded-lg border-2 border-amber-300 bg-amber-50 px-4 py-3">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" aria-hidden="true" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-amber-900">
                Tu caja lleva {turno.dias} días abierta
              </div>
              <p className="mt-1 text-sm text-amber-900">
                Desde el{' '}
                {new Date(turno.desde).toLocaleDateString(locale, {
                  weekday: 'long',
                  day: '2-digit',
                  month: 'long',
                })}{' '}
                llevas <strong>{formatPrecio(turno.total, monedaSimbolo, locale)}</strong> sin
                cortar. Ese total NO es el de arriba: arriba está sólo {tituloPeriodo.toLowerCase()}
                .
              </p>
              {puedeCerrarElDia && (
                <Link
                  to="/cerrar-dia"
                  className="btn-ghost mt-2 inline-flex min-h-[44px] border border-amber-400 text-sm"
                >
                  Terminar mi día
                </Link>
              )}
            </div>
          </div>
        </div>
      )}

      {ventas.length > 8 && (
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500"
            aria-hidden="true"
          />
          <input
            className="input pl-9"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar por folio o por nombre"
            aria-label="Buscar una venta"
          />
        </div>
      )}

      {truncado && (
        <div className="rounded-lg border border-slate-300 bg-slate-50 px-4 py-2 text-sm text-slate-700">
          Son demasiadas ventas para mostrarlas todas. Estás viendo las más recientes; elige un
          periodo más corto para ver el total exacto.
        </div>
      )}

      {error ? (
        <div className="card border-2 border-amber-300 bg-amber-50 text-amber-900">
          No pude traer tus ventas. {error}
        </div>
      ) : cargando ? (
        <div className="card py-8 text-center text-slate-500">Cargando tus ventas…</div>
      ) : filtradas.length === 0 ? (
        <div className="card py-8 text-center text-slate-600">
          {ventas.length === 0
            ? `No cobraste nada en este periodo (${tituloPeriodo.toLowerCase()}).`
            : 'Ninguna venta coincide con lo que escribiste.'}
        </div>
      ) : (
        <div className="space-y-4">
          {porDia.map(([dia, delDia]) => (
            <section key={dia} className="space-y-2">
              {/* Un solo día: la fecha ya está arriba y repetirla aquí sería ruido. */}
              {porDia.length > 1 && (
                <h2 className="px-1 text-sm font-bold uppercase tracking-wide text-slate-600">
                  {diaLargo(dia)}
                </h2>
              )}
              <ul className="space-y-2">
                {delDia.map((v) => (
                  <li key={v.id}>
                    {/*
                      La fila entera es el botón: quien busca su venta para corregirla no tiene
                      por qué acertarle a un icono de 20 px con el cliente enfrente.
                    */}
                    <button
                      onClick={() => setDetalleId(v.id)}
                      className="card flex w-full min-h-[64px] items-center gap-3 text-left transition hover:border-primary"
                    >
                      <div className="w-20 shrink-0 font-mono text-sm font-bold">
                        #{String(v.numeroPedido).padStart(5, '0')}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-semibold">
                          {v.clienteNombre ?? 'Sin nombre'}
                        </div>
                        <div className="text-sm text-slate-600">
                          {/*
                            Sprint 147 — La fecha va junto a la hora siempre que el periodo tenga
                            más de un día. Sin ella, "01:47 p.m." se lee como "hoy", que es
                            exactamente la confusión que costó este sprint.
                          */}
                          {porDia.length > 1
                            ? `${new Date(v.fechaCreacion).toLocaleDateString(locale, {
                                day: '2-digit',
                                month: 'short',
                              })} · ${hora(v.fechaCreacion)}`
                            : hora(v.fechaCreacion)}
                          {v.metodoPago ? ` · ${v.metodoPago}` : ''}
                        </div>
                      </div>
                      {/* Lo que falta por cobrar no se esconde en la lista del dinero. */}
                      {(v.faltaCobrar ?? 0) > 0 && (
                        <span className="rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-800">
                          Falta {formatPrecio(v.faltaCobrar ?? 0, monedaSimbolo, locale)}
                        </span>
                      )}
                      {/*
                        Sprint 146 — Y el dinero que le sobra al cliente, tampoco. Una deuda se
                        marcaba en la lista y un saldo a favor no: había que abrir la venta para
                        enterarse, así que una devolución pendiente podía pasar el día entero sin
                        que nadie la viera.
                      */}
                      {(v.montoPorDevolver ?? 0) > 0 && (
                        <span className="rounded-full bg-violet-100 px-2 py-1 text-xs font-semibold text-violet-900">
                          A favor {formatPrecio(v.montoPorDevolver ?? 0, monedaSimbolo, locale)}
                        </span>
                      )}
                      <div className="font-mono text-lg font-bold">
                        {formatPrecio(v.total, monedaSimbolo, locale)}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      <DetallePedidoModal
        abierto={detalleId !== null}
        pedidoId={detalleId}
        onClose={() => {
          setDetalleId(null);
          void cargar();
        }}
        // Si corrigió algo dentro, la lista de atrás tiene que decir lo nuevo, no lo de antes.
        onItemCancelado={() => void cargar()}
      />
    </div>
  );
}
